import React, { useState, useRef, useEffect } from "react";
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, X, Check } from "lucide-react";

const QUICK_PRESETS = [
  { id: "custom", label: "Custom Range" },
  { id: "today", label: "Today" },
  { id: "last_7_days", label: "Last 7 Days" },
  { id: "this_week", label: "This Week" },
  { id: "last_week", label: "Last Week" },
  { id: "this_month", label: "This Month" },
  { id: "last_30_days", label: "Last 30 Days" },
  { id: "last_month", label: "Last Month" },
  { id: "this_quarter", label: "This Quarter" },
  { id: "all_time", label: "All Time" },
];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function formatDateDisplay(d) {
  if (!d) return "";
  const month = MONTH_NAMES[d.getMonth()];
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month} ${day}, ${year}`;
}

function formatDateIso(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseJobDateTimestamp(postedStr) {
  if (!postedStr) return Date.now();
  const s = String(postedStr).trim().toLowerCase();
  const now = new Date();

  if (s === "today") return now.getTime();
  if (s === "yesterday") {
    const d = new Date(now);
    d.setDate(d.getDate() - 1);
    return d.getTime();
  }
  if (s === "recently" || s === "in tracker") return now.getTime();

  const daysMatch = s.match(/(\d+)\s*(?:d|day|days)\s*ago/);
  if (daysMatch) {
    const d = new Date(now);
    d.setDate(d.getDate() - parseInt(daysMatch[1], 10));
    return d.getTime();
  }

  const weeksMatch = s.match(/(\d+)\s*(?:w|week|weeks)\s*ago/);
  if (weeksMatch) {
    const d = new Date(now);
    d.setDate(d.getDate() - parseInt(weeksMatch[1], 10) * 7);
    return d.getTime();
  }

  const monthsMatch = s.match(/(\d+)\s*(?:m|month|months)\s*ago/);
  if (monthsMatch) {
    const d = new Date(now);
    d.setMonth(d.getMonth() - parseInt(monthsMatch[1], 10));
    return d.getTime();
  }

  // Check YYYY-MM-DD or standard parseable ISO
  const isoMatch = s.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const parsed = new Date(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10) - 1, parseInt(isoMatch[3], 10));
    if (!isNaN(parsed.getTime())) return parsed.getTime();
  }

  const directParsed = Date.parse(postedStr);
  if (!isNaN(directParsed)) return directParsed;

  return now.getTime();
}

export function computePresetDates(presetId) {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

  switch (presetId) {
    case "today":
      return { start: startOfDay, end: endOfDay };
    case "last_7_days": {
      const l7Start = new Date(startOfDay);
      l7Start.setDate(l7Start.getDate() - 6);
      return { start: l7Start, end: endOfDay };
    }
    case "this_week": {
      const currentDay = startOfDay.getDay(); // 0 is Sunday
      const diff = currentDay === 0 ? -6 : 1 - currentDay; // Monday
      const mon = new Date(startOfDay);
      mon.setDate(mon.getDate() + diff);
      return { start: mon, end: endOfDay };
    }
    case "last_week": {
      const currentDay = startOfDay.getDay();
      const diff = currentDay === 0 ? -6 : 1 - currentDay;
      const lastMon = new Date(startOfDay);
      lastMon.setDate(lastMon.getDate() + diff - 7);
      const lastSun = new Date(lastMon);
      lastSun.setDate(lastSun.getDate() + 6);
      lastSun.setHours(23, 59, 59);
      return { start: lastMon, end: lastSun };
    }
    case "this_month": {
      const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: mStart, end: endOfDay };
    }
    case "last_30_days": {
      const l30Start = new Date(startOfDay);
      l30Start.setDate(l30Start.getDate() - 29);
      return { start: l30Start, end: endOfDay };
    }
    case "last_month": {
      const lmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lmEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      return { start: lmStart, end: lmEnd };
    }
    case "this_quarter": {
      const qMonth = Math.floor(now.getMonth() / 3) * 3;
      const qStart = new Date(now.getFullYear(), qMonth, 1);
      return { start: qStart, end: endOfDay };
    }
    case "all_time":
    default:
      return { start: null, end: null };
  }
}

function CalendarMonth({ year, month, selectedStart, selectedEnd, onSelectDate, onPrevMonth, onNextMonth }) {
  const firstDayIndex = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const days = [];

  // Prev month padding
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    days.push({
      day: daysInPrevMonth - i,
      month: month - 1,
      year: month === 0 ? year - 1 : year,
      isCurrentMonth: false,
    });
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({
      day: d,
      month,
      year,
      isCurrentMonth: true,
    });
  }

  // Next month padding to fill 35 or 42 grid cells
  const remaining = (7 - (days.length % 7)) % 7;
  for (let d = 1; d <= remaining; d++) {
    days.push({
      day: d,
      month: month + 1,
      year: month === 11 ? year + 1 : year,
      isCurrentMonth: false,
    });
  }

  const isSelectedStart = (cellDate) => {
    if (!selectedStart) return false;
    return (
      cellDate.getDate() === selectedStart.getDate() &&
      cellDate.getMonth() === selectedStart.getMonth() &&
      cellDate.getFullYear() === selectedStart.getFullYear()
    );
  };

  const isSelectedEnd = (cellDate) => {
    if (!selectedEnd) return false;
    return (
      cellDate.getDate() === selectedEnd.getDate() &&
      cellDate.getMonth() === selectedEnd.getMonth() &&
      cellDate.getFullYear() === selectedEnd.getFullYear()
    );
  };

  const isInRange = (cellDate) => {
    if (!selectedStart || !selectedEnd) return false;
    const t = cellDate.getTime();
    const s = new Date(selectedStart.getFullYear(), selectedStart.getMonth(), selectedStart.getDate()).getTime();
    const e = new Date(selectedEnd.getFullYear(), selectedEnd.getMonth(), selectedEnd.getDate(), 23, 59, 59).getTime();
    return t >= s && t <= e;
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between pb-2 mb-2 border-b border-border/60">
        <button
          type="button"
          onClick={onPrevMonth}
          className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-xs font-bold font-heading">
          {MONTH_NAMES[month]} {year}
        </span>
        <button
          type="button"
          onClick={onNextMonth}
          className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-muted-foreground mb-1">
        <span>S</span>
        <span>M</span>
        <span>T</span>
        <span>W</span>
        <span>T</span>
        <span>F</span>
        <span>S</span>
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1 text-center text-xs">
        {days.map((item, idx) => {
          const cellDate = new Date(item.year, item.month, item.day);
          const isStart = isSelectedStart(cellDate);
          const isEnd = isSelectedEnd(cellDate);
          const inRange = isInRange(cellDate);

          let bgClass = "hover:bg-secondary/80 text-foreground";
          if (!item.isCurrentMonth) {
            bgClass = "text-muted-foreground/40 hover:bg-secondary/40";
          }
          if (inRange) {
            bgClass = "bg-electric/15 text-electric";
          }
          if (isStart || isEnd) {
            bgClass = "bg-electric text-white font-bold shadow-xs";
          }

          return (
            <button
              key={idx}
              type="button"
              onClick={() => onSelectDate(cellDate)}
              className={`h-7 w-7 mx-auto flex items-center justify-center rounded-lg text-xs transition-all ${bgClass}`}
            >
              {item.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DateRangeFilter({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const initialStart = value?.start ? new Date(value.start) : null;
  const initialEnd = value?.end ? new Date(value.end) : null;

  const [selectedPreset, setSelectedPreset] = useState(value?.preset || "all_time");
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);

  const now = new Date();
  const [leftCalMonth, setLeftCalMonth] = useState(initialStart ? initialStart.getMonth() : now.getMonth());
  const [leftCalYear, setLeftCalYear] = useState(initialStart ? initialStart.getFullYear() : now.getFullYear());

  const [rightCalMonth, setRightCalMonth] = useState(
    initialEnd
      ? initialEnd.getMonth()
      : (now.getMonth() === 11 ? 0 : now.getMonth() + 1)
  );
  const [rightCalYear, setRightCalYear] = useState(
    initialEnd
      ? initialEnd.getFullYear()
      : (now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear())
  );

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Sync internal state when prop value changes
  useEffect(() => {
    if (value) {
      setSelectedPreset(value.preset || "all_time");
      setStartDate(value.start ? new Date(value.start) : null);
      setEndDate(value.end ? new Date(value.end) : null);
    }
  }, [value]);

  const handleSelectPreset = (presetId) => {
    setSelectedPreset(presetId);
    if (presetId === "custom") return;

    const { start, end } = computePresetDates(presetId);
    setStartDate(start);
    setEndDate(end);

    if (start) {
      setLeftCalMonth(start.getMonth());
      setLeftCalYear(start.getFullYear());
    }
    if (end) {
      setRightCalMonth(end.getMonth());
      setRightCalYear(end.getFullYear());
    }
  };

  const handleSelectDate = (date) => {
    setSelectedPreset("custom");
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(startDate);
      } else {
        setEndDate(date);
      }
    }
  };

  const handleApply = () => {
    let finalEnd = endDate;
    if (startDate && !endDate) {
      finalEnd = new Date(startDate);
      finalEnd.setHours(23, 59, 59);
    }

    onChange?.({
      preset: selectedPreset,
      start: startDate ? startDate.toISOString() : null,
      end: finalEnd ? finalEnd.toISOString() : null,
      label: getDisplayLabel(selectedPreset, startDate, finalEnd),
    });
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedPreset("all_time");
    setStartDate(null);
    setEndDate(null);
    onChange?.({
      preset: "all_time",
      start: null,
      end: null,
      label: null,
    });
    setOpen(false);
  };

  function getDisplayLabel(preset, start, end) {
    if (!start && !end) return "Filter by Date";
    const found = QUICK_PRESETS.find((p) => p.id === preset);
    if (found && found.id !== "custom" && found.id !== "all_time") {
      return found.label;
    }
    if (start && end) {
      return `${formatDateDisplay(start)} – ${formatDateDisplay(end)}`;
    }
    if (start) {
      return `From ${formatDateDisplay(start)}`;
    }
    return "Filter by Date";
  }

  const isFilterActive = Boolean(startDate || endDate || (selectedPreset && selectedPreset !== "all_time"));
  const activeLabel = value?.label || (isFilterActive ? getDisplayLabel(selectedPreset, startDate, endDate) : "Filter by Date");

  return (
    <div className="relative inline-block" ref={containerRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs sm:text-sm font-semibold transition-all shadow-xs ${
          isFilterActive && selectedPreset !== "all_time"
            ? "border-electric bg-electric/10 text-electric hover:bg-electric/15"
            : "border-border bg-card text-foreground hover:bg-secondary"
        }`}
      >
        <CalendarIcon className={`h-4 w-4 ${isFilterActive && selectedPreset !== "all_time" ? "text-electric" : "text-muted-foreground"}`} />
        <span className="truncate max-w-[170px] sm:max-w-[220px]">{activeLabel}</span>
        {isFilterActive && selectedPreset !== "all_time" ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="p-0.5 rounded-full hover:bg-electric/20 text-electric ml-0.5"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        ) : (
          <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>

      {/* Popover Card */}
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-[92vw] sm:w-[570px] max-w-[calc(100vw-1.5rem)] rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-float animate-in fade-in zoom-in-95 duration-150">
          {/* Header */}
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-border/70">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-electric" />
              <h4 className="font-heading text-sm font-bold">Filter by Date</h4>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
            {/* Quick Filters Left Column */}
            <div className="sm:col-span-4 border-b sm:border-b-0 sm:border-r border-border/70 pb-3 sm:pb-0 sm:pr-3">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                Quick Filters
              </p>
              <div className="flex flex-col gap-1 max-h-[220px] sm:max-h-[300px] overflow-y-auto pr-1">
                {QUICK_PRESETS.map((preset) => {
                  const isSelected = selectedPreset === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => handleSelectPreset(preset.id)}
                      className={`flex items-center justify-between rounded-xl px-3 py-1.5 text-xs font-semibold text-left transition-all ${
                        isSelected
                          ? "bg-electric text-white shadow-xs font-bold"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                      }`}
                    >
                      <span>{preset.label}</span>
                      {isSelected && <Check className="h-3.5 w-3.5 stroke-[2.5]" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Calendars Right Column */}
            <div className="sm:col-span-8 flex flex-col justify-between space-y-3">
              {/* From / To Date Display Boxes */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-border bg-secondary/30 p-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-0.5">
                    From
                  </label>
                  <div className="flex items-center gap-1.5 text-xs font-medium truncate">
                    <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{startDate ? formatDateDisplay(startDate) : "Select date"}</span>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-secondary/30 p-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-0.5">
                    To
                  </label>
                  <div className="flex items-center gap-1.5 text-xs font-medium truncate">
                    <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{endDate ? formatDateDisplay(endDate) : "Select date"}</span>
                  </div>
                </div>
              </div>

              {/* Calendars Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <CalendarMonth
                  year={leftCalYear}
                  month={leftCalMonth}
                  selectedStart={startDate}
                  selectedEnd={endDate}
                  onSelectDate={handleSelectDate}
                  onPrevMonth={() => {
                    if (leftCalMonth === 0) {
                      setLeftCalMonth(11);
                      setLeftCalYear((y) => y - 1);
                    } else {
                      setLeftCalMonth((m) => m - 1);
                    }
                  }}
                  onNextMonth={() => {
                    if (leftCalMonth === 11) {
                      setLeftCalMonth(0);
                      setLeftCalYear((y) => y + 1);
                    } else {
                      setLeftCalMonth((m) => m + 1);
                    }
                  }}
                />

                <div className="hidden sm:block">
                  <CalendarMonth
                    year={rightCalYear}
                    month={rightCalMonth}
                    selectedStart={startDate}
                    selectedEnd={endDate}
                    onSelectDate={handleSelectDate}
                    onPrevMonth={() => {
                      if (rightCalMonth === 0) {
                        setRightCalMonth(11);
                        setRightCalYear((y) => y - 1);
                      } else {
                        setRightCalMonth((m) => m - 1);
                      }
                    }}
                    onNextMonth={() => {
                      if (rightCalMonth === 11) {
                        setRightCalMonth(0);
                        setRightCalYear((y) => y + 1);
                      } else {
                        setRightCalMonth((m) => m + 1);
                      }
                    }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/70">
                <button
                  type="button"
                  onClick={handleClear}
                  className="rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  className="cv-btn-primary py-2 px-5 text-xs font-bold shadow-xs"
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
